import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export function useAdminStatus() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Add refresh function
  const refreshAdminStatus = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    async function checkAdminStatus() {
      try {
        setIsLoading(true);

        // Get the current user ID first
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user?.id) {
          setIsAdmin(false);
          return;
        }

        // Use the user ID to query the profile
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("organization_id")
          .eq("id", userData.user.id)
          .single();

        if (profileError) throw profileError;
        if (!profileData?.organization_id) {
          setIsAdmin(false);
          return;
        }

        // Get organization details to check super admin status
        const { data: orgData, error: orgError } = await supabase
          .from("organizations")
          .select("is_super_admin")
          .eq("id", profileData.organization_id)
          .single();

        if (orgError) throw orgError;

        setIsAdmin(orgData?.is_super_admin || false);
      } catch (err: any) {
        console.error("Error checking admin status:", err);
        setError(err);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkAdminStatus();
  }, [refreshTrigger]);

  return { isAdmin, isLoading, error, refreshAdminStatus };
}
